import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { meQuery } from '@/resources/auth/auth.service';
import {
  organizationsQuery,
  useOrganization,
  useOrganizations,
} from '@/resources/organization/organization.service';
import { albumsQuery, useAlbum } from '@/resources/album/album.service';
import { debtsQuery, useDebt } from '@/resources/debt/debt.service';
import { remindersQuery, useReminders } from '@/resources/reminder/reminder.service';
import { getToken, setToken } from '@/resources/token';
import { LoginCard } from '@/auth/login-card';
import { AppShell } from '@/dashboard/app-shell';
import { AccountSection } from '@/dashboard/account-section';
import { CreateOrganizationSection } from '@/dashboard/create-organization-section';
import { OverviewSection } from '@/dashboard/overview-section';
import { DebtDetail, DebtDetailSkeleton } from '@/dashboard/debt-detail';
import { DebtDialog, DebtsSection } from '@/dashboard/debts-section';
import { MembersSection } from '@/dashboard/members-section';
import { AlbumDetail } from '@/dashboard/album-detail';
import { AlbumsSection } from '@/dashboard/albums-section';
import { PhotosSection } from '@/dashboard/photos-section';
import { SharePage } from '@/share/share-page';
import { ReminderDialog, RemindersSection } from '@/dashboard/reminders-section';
import { NotFoundPage, RouteErrorPage } from '@/dashboard/route-fallbacks';
import { JoinPage } from '@/invite/join-page';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    titleKey?: ParseKeys;
  }
}

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  beforeLoad: () => {
    if (getToken()) throw redirect({ to: '/' });
  },
  component: LoginRoute,
});

function LoginRoute() {
  const { next } = loginRoute.useSearch();
  const navigate = useNavigate();
  return <LoginCard onAuthenticated={() => navigate({ href: next ?? '/' })} />;
}

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$token',
  component: JoinRoute,
});

function JoinRoute() {
  const { token } = joinRoute.useParams();
  const navigate = useNavigate();
  return <JoinPage token={token} onDone={() => navigate({ to: '/' })} />;
}

const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: ShareRoute,
});

function ShareRoute() {
  const { token } = shareRoute.useParams();
  return <SharePage token={token} />;
}

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    const toLogin = redirect({ to: '/login', search: { next: location.href } });
    if (!getToken()) throw toLogin;

    try {
      const { user } = await context.queryClient.ensureQueryData(meQuery());
      return { user };
    } catch {
      setToken(null);
      throw toLogin;
    }
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const { organizations } = await context.queryClient.ensureQueryData(
      organizationsQuery(),
    );
    const first = organizations[0];
    throw first
      ? redirect({
          to: '/organizations/$organizationId',
          params: { organizationId: first.id },
        })
      : redirect({ to: '/organizations/new' });
  },
});

const accountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/account',
  staticData: { titleKey: 'nav.account' },
  component: AccountRoute,
});

function AccountRoute() {
  const { user } = authedRoute.useRouteContext();
  return <AccountSection user={user} />;
}

const newOrganizationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/organizations/new',
  staticData: { titleKey: 'nav.newOrganization' },
  component: NewOrganizationRoute,
});

function NewOrganizationRoute() {
  const organizations = useOrganizations();
  const navigate = useNavigate();
  return (
    <CreateOrganizationSection
      first={(organizations.data?.length ?? 0) === 0}
      onCreated={(organizationId) =>
        navigate({
          to: '/organizations/$organizationId',
          params: { organizationId },
        })
      }
    />
  );
}

const organizationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/organizations/$organizationId',
  beforeLoad: async ({ context, params }) => {
    const has = (data: { organizations: Array<{ id: string }> }) =>
      data.organizations.some((org) => org.id === params.organizationId);

    if (has(await context.queryClient.ensureQueryData(organizationsQuery()))) return;
    if (has(await context.queryClient.fetchQuery(organizationsQuery()))) return;
    throw redirect({ to: '/' });
  },
  component: Outlet,
});

/**
 * Erinnerungen und Schulden gehen Freunde nichts an. Die Sidebar blendet beides
 * schon aus, das hier fängt die direkt eingetippte Adresse ab.
 */
async function blockFriends(
  queryClient: QueryClient,
  organizationId: string,
): Promise<void> {
  const { organizations } = await queryClient.ensureQueryData(organizationsQuery());
  const organization = organizations.find((org) => org.id === organizationId);
  if (organization?.role === 'friend') {
    throw redirect({
      to: '/organizations/$organizationId',
      params: { organizationId },
    });
  }
}

const organizationIndexRoute = createRoute({
  getParentRoute: () => organizationRoute,
  path: '/',
  staticData: { titleKey: 'overview.title' },
  component: OverviewRoute,
});

function OverviewRoute() {
  const { organizationId } = organizationIndexRoute.useParams();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return <OverviewSection organization={organization} />;
}

const membersRoute = createRoute({
  getParentRoute: () => organizationRoute,
  path: 'members',
  staticData: { titleKey: 'nav.members' },
  component: MembersRoute,
});

function MembersRoute() {
  const { organizationId } = membersRoute.useParams();
  const { user } = authedRoute.useRouteContext();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return <MembersSection organization={organization} user={user} />;
}

const photosRoute = createRoute({
  getParentRoute: () => organizationRoute,
  path: 'photos',
  staticData: { titleKey: 'photos.title' },
  component: Outlet,
});

const photosIndexRoute = createRoute({
  getParentRoute: () => photosRoute,
  path: '/',
  component: PhotosRoute,
});

function PhotosRoute() {
  const { organizationId } = photosIndexRoute.useParams();
  const { user } = authedRoute.useRouteContext();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return <PhotosSection organization={organization} user={user} />;
}

const albumsRoute = createRoute({
  getParentRoute: () => photosRoute,
  path: 'albums',
  staticData: { titleKey: 'albums.title' },
  component: Outlet,
});

const albumsIndexRoute = createRoute({
  getParentRoute: () => albumsRoute,
  path: '/',
  component: AlbumsRoute,
});

function AlbumsRoute() {
  const { organizationId } = albumsIndexRoute.useParams();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return <AlbumsSection organization={organization} />;
}

const albumDetailRoute = createRoute({
  getParentRoute: () => albumsRoute,
  path: '$albumId',
  loader: async ({ context, params }) => {
    const { albums } = await context.queryClient.ensureQueryData(
      albumsQuery(params.organizationId),
    );
    if (!albums.some((album) => album.id === params.albumId)) {
      throw redirect({
        to: '/organizations/$organizationId/photos/albums',
        params: { organizationId: params.organizationId },
      });
    }
  },
  component: AlbumDetailRoute,
});

function AlbumDetailRoute() {
  const { organizationId, albumId } = albumDetailRoute.useParams();
  const { user } = authedRoute.useRouteContext();
  const organization = useOrganization(organizationId).data;
  const album = useAlbum(organizationId, albumId).data;
  if (!organization || !album) return null;
  return <AlbumDetail organization={organization} user={user} album={album} />;
}

const remindersRoute = createRoute({
  getParentRoute: () => organizationRoute,
  path: 'reminders',
  staticData: { titleKey: 'reminders.title' },
  beforeLoad: ({ context, params }) =>
    blockFriends(context.queryClient, params.organizationId),
  component: RemindersRoute,
});

function RemindersRoute() {
  const { organizationId } = remindersRoute.useParams();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return (
    <>
      <RemindersSection organization={organization} />
      <Outlet />
    </>
  );
}

const newReminderRoute = createRoute({
  getParentRoute: () => remindersRoute,
  path: 'new',
  component: NewReminderRoute,
});

function NewReminderRoute() {
  const { organizationId } = newReminderRoute.useParams();
  const organization = useOrganization(organizationId).data;
  const close = useCloseDialog(organizationId);
  if (!organization) return null;
  return <ReminderDialog organization={organization} onClose={close} />;
}

const editReminderRoute = createRoute({
  getParentRoute: () => remindersRoute,
  path: '$reminderId/edit',
  loader: async ({ context, params }) => {
    const { reminders } = await context.queryClient.ensureQueryData(
      remindersQuery(params.organizationId),
    );
    if (!reminders.some((reminder) => reminder.id === params.reminderId)) {
      throw redirect({
        to: '/organizations/$organizationId/reminders',
        params: { organizationId: params.organizationId },
      });
    }
  },
  component: EditReminderRoute,
});

function EditReminderRoute() {
  const { organizationId, reminderId } = editReminderRoute.useParams();
  const organization = useOrganization(organizationId).data;
  const reminder = useReminders(organizationId).data?.find((r) => r.id === reminderId);
  const close = useCloseDialog(organizationId);
  if (!organization || !reminder) return null;
  return (
    <ReminderDialog organization={organization} reminder={reminder} onClose={close} />
  );
}

function useCloseDialog(organizationId: string): () => void {
  const navigate = useNavigate();
  return () =>
    void navigate({
      to: '/organizations/$organizationId/reminders',
      params: { organizationId },
    });
}

const debtsRoute = createRoute({
  getParentRoute: () => organizationRoute,
  path: 'debts',
  staticData: { titleKey: 'debts.title' },
  beforeLoad: ({ context, params }) =>
    blockFriends(context.queryClient, params.organizationId),
  component: Outlet,
});

const debtsIndexRoute = createRoute({
  getParentRoute: () => debtsRoute,
  path: '/',
  component: DebtsListRoute,
});

function DebtsListRoute() {
  const { organizationId } = debtsIndexRoute.useParams();
  const organization = useOrganization(organizationId).data;
  if (!organization) return null;
  return <DebtsSection organization={organization} />;
}

const newDebtRoute = createRoute({
  getParentRoute: () => debtsRoute,
  path: 'new',
  component: NewDebtRoute,
});

function NewDebtRoute() {
  const { organizationId } = newDebtRoute.useParams();
  const navigate = useNavigate();
  const organization = useOrganization(organizationId).data;
  const close = useCloseDebtDialog(organizationId);
  if (!organization) return null;
  return (
    <>
      <DebtsSection organization={organization} />
      <DebtDialog
        organization={organization}
        onClose={close}
        onCreated={(debtId) =>
          void navigate({
            to: '/organizations/$organizationId/debts/$debtId',
            params: { organizationId, debtId },
          })
        }
      />
    </>
  );
}

const debtDetailRoute = createRoute({
  getParentRoute: () => debtsRoute,
  path: '$debtId',
  loader: async ({ context, params }) => {
    const { debts } = await context.queryClient.ensureQueryData(
      debtsQuery(params.organizationId),
    );
    if (!debts.some((debt) => debt.id === params.debtId)) {
      throw redirect({
        to: '/organizations/$organizationId/debts',
        params: { organizationId: params.organizationId },
      });
    }
  },
  component: DebtDetailRoute,
});

function DebtDetailRoute() {
  const { organizationId, debtId } = debtDetailRoute.useParams();
  const organization = useOrganization(organizationId).data;
  const debt = useDebt(organizationId, debtId).data;
  if (!organization || !debt) return <DebtDetailSkeleton />;
  return <DebtDetail organization={organization} debt={debt} />;
}

function useCloseDebtDialog(organizationId: string): () => void {
  const navigate = useNavigate();
  return () =>
    void navigate({
      to: '/organizations/$organizationId/debts',
      params: { organizationId },
    });
}

const routeTree = rootRoute.addChildren([
  loginRoute,
  joinRoute,
  shareRoute,
  authedRoute.addChildren([
    indexRoute,
    accountRoute,
    newOrganizationRoute,
    organizationRoute.addChildren([
      organizationIndexRoute,
      membersRoute,
      photosRoute.addChildren([
        photosIndexRoute,
        albumsRoute.addChildren([albumsIndexRoute, albumDetailRoute]),
      ]),
      remindersRoute.addChildren([newReminderRoute, editReminderRoute]),
      debtsRoute.addChildren([debtsIndexRoute, newDebtRoute, debtDetailRoute]),
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    defaultPendingComponent: PendingRoute,
    defaultErrorComponent: ({ error, reset }) => (
      <RouteErrorPage error={error} reset={reset} />
    ),
  });
}

function PendingRoute() {
  const { t } = useTranslation();
  return <p className="p-6 text-sm text-muted-foreground">{t('common.oneMoment')}</p>;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
